// =============================================================================
// useVisualViewportInset — the on-screen keyboard, measured (farm phone/tablet
// spec W-12, audit X-01).
//
// A keyboard takes the bottom of the screen in one of two ways, and the app
// has to survive both:
//
//   • The LAYOUT shrinks. index.html's viewport meta asks for this
//     (`interactive-widget=resizes-content`, decision D3): Android Chrome ≥108
//     resizes the page itself, so `position: fixed; bottom: 0` lands just above
//     the keys and `dvh` shrinks with it. Nothing to lift — but a fixed bottom
//     bar now rides up with the keys and eats the little room left, so
//     BottomNav asks `useKeyboardOpen()` and steps aside.
//   • Only the VISUAL viewport shrinks. iOS Safari ignores the meta key, and so
//     does any Android browser older than it: the layout keeps its full height,
//     the keys cover its bottom, and a bottom sheet's footer — Save — sits
//     under them. `--kb-inset` is how many px of the layout the keyboard
//     covers, `--vv-top` how far the browser has panned the visible area down;
//     <Modal> fits its backdrop between the two.
//
// Both variables are published on <html> while anything subscribes and removed
// when the last subscriber goes, so a page with no Modal and no BottomNav is
// untouched. A pinch-zoomed page also has a small visual viewport — but that
// is the person zooming, not a keyboard, so any scale other than 1 reads as
// "nothing to make room for" and the page behaves exactly as before.
//
// Lifted from SearchableSelect (its touch sheet already sized itself to the
// visible viewport — the one component that got this right); the sheet still
// uses `visibleViewport` + `useViewportListener` below, unchanged.
// =============================================================================

import { useEffect, useLayoutEffect, useSyncExternalStore } from 'react'

/** The visible part of the page: its top edge and height, in layout px. */
export const visibleViewport = () => {
  const v = typeof window !== 'undefined' ? window.visualViewport : null
  return v ? { top: v.offsetTop, height: v.height } : { top: 0, height: window.innerHeight }
}

/** Re-run `update` while `on` whenever the visible area moves — resize,
 *  scroll, or an on-screen keyboard opening. */
export function useViewportListener(on: boolean, update: () => void) {
  useLayoutEffect(() => {
    if (!on) return
    update()
    const v = window.visualViewport
    v?.addEventListener('resize', update)
    v?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      v?.removeEventListener('resize', update)
      v?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [on, update])
}

// -----------------------------------------------------------------------------
// The maths — pure, so the rules are pinned by tests rather than a device.
// -----------------------------------------------------------------------------

type ViewportLike = Pick<VisualViewport, 'height' | 'offsetTop' | 'scale'>

/** The part of the LAYOUT viewport the visible area leaves out: `top` px above
 *  it (the browser panned down to a field), `inset` px below it (the keys).
 *  Both 0 when nothing is shrunk, when there is no visualViewport, or when the
 *  page is pinch-zoomed — zoom is the person, not a keyboard. */
export function viewportGap(vv: ViewportLike | null | undefined, innerHeight: number): { top: number; inset: number } {
  if (!vv || Math.abs(vv.scale - 1) > 0.01) return { top: 0, inset: 0 }
  return {
    top: Math.max(0, Math.round(vv.offsetTop)),
    inset: Math.max(0, Math.round(innerHeight - vv.offsetTop - vv.height)),
  }
}

// The smallest keyboard is a landscape phone's (~150px); an iPad's hardware-
// keyboard shortcut bar (~55px) or a browser toolbar settling is not one.
const KEYBOARD_MIN_PX = 120

/** Is an on-screen keyboard (very probably) up? A field that takes typing has
 *  focus AND the screen lost a keyboard's worth of height — from the visual
 *  viewport (`inset`) or, under resizes-content, from the layout itself
 *  (`shrunkBy`: the tallest height seen at this width minus the current one).
 *  Focus alone is not enough (a hardware keyboard, a scanner wedge); a short
 *  window alone is not enough either (Android split-screen). */
export function keyboardLikelyOpen(p: { inset: number; shrunkBy: number; typingFocused: boolean }): boolean {
  return p.typingFocused && Math.max(p.inset, p.shrunkBy) >= KEYBOARD_MIN_PX
}

// Inputs that never raise a text keyboard.
const NO_KEYBOARD_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'color', 'range', 'image', 'hidden'])

/** Does `el` raise a keyboard when focused? */
export function takesTyping(el: Element | null): boolean {
  if (!el) return false
  if ((el as HTMLElement).isContentEditable) return true
  if (el.getAttribute('inputmode') === 'none') return false
  if (el.tagName === 'TEXTAREA') return true
  return el.tagName === 'INPUT' && !NO_KEYBOARD_TYPES.has((el as HTMLInputElement).type)
}

// -----------------------------------------------------------------------------
// One shared store: listeners attach for the first subscriber and detach
// after the last, so N open Modals + BottomNav cost one set of listeners.
// -----------------------------------------------------------------------------

interface Snapshot { top: number; inset: number; open: boolean }
const CLOSED: Snapshot = { top: 0, inset: 0, open: false }

let snap: Snapshot = CLOSED
let tallest = { width: 0, height: 0 }
let watched: VisualViewport | null = null
let deferred = 0
const subscribers = new Set<() => void>()

function measureNow(): Snapshot {
  const { innerWidth: w, innerHeight: h } = window
  tallest = w === tallest.width ? { width: w, height: Math.max(tallest.height, h) } : { width: w, height: h }
  const gap = viewportGap(window.visualViewport, h)
  const open = keyboardLikelyOpen({ inset: gap.inset, shrunkBy: tallest.height - h, typingFocused: takesTyping(document.activeElement) })
  return { ...gap, open }
}

function publish(s: Snapshot) {
  const root = document.documentElement.style
  root.setProperty('--kb-inset', `${s.inset}px`)
  root.setProperty('--vv-top', `${s.top}px`)
}

function refresh() {
  const next = measureNow()
  if (next.top === snap.top && next.inset === snap.inset && next.open === snap.open) return
  snap = next
  publish(snap)
  subscribers.forEach(fn => fn())
}

// focusout fires before the next element takes focus — measure after it lands.
function refreshSoon() {
  window.clearTimeout(deferred)
  deferred = window.setTimeout(refresh, 0)
}

function attach() {
  tallest = { width: window.innerWidth, height: window.innerHeight }
  watched = window.visualViewport
  watched?.addEventListener('resize', refresh)
  watched?.addEventListener('scroll', refresh)
  window.addEventListener('resize', refresh)
  document.addEventListener('focusin', refreshSoon)
  document.addEventListener('focusout', refreshSoon)
  publish(snap)
  refresh()
}

function detach() {
  watched?.removeEventListener('resize', refresh)
  watched?.removeEventListener('scroll', refresh)
  watched = null
  window.removeEventListener('resize', refresh)
  document.removeEventListener('focusin', refreshSoon)
  document.removeEventListener('focusout', refreshSoon)
  window.clearTimeout(deferred)
  snap = CLOSED
  const root = document.documentElement.style
  root.removeProperty('--kb-inset')
  root.removeProperty('--vv-top')
}

function subscribe(onChange: () => void): () => void {
  subscribers.add(onChange)
  if (subscribers.size === 1) attach()
  return () => {
    subscribers.delete(onChange)
    if (subscribers.size === 0) detach()
  }
}

/** Keep `--kb-inset` / `--vv-top` published on <html> while the caller is
 *  mounted. Returns nothing and never re-renders its caller: CSS reads the
 *  variables, so a keyboard sliding up does not re-render a whole form.
 *  ⚠️ A FRESH callback per mount: the subscriber set is a Set, so two open
 *  Modals sharing one function would count as one, and the first to close
 *  would unpublish the inset under the other. */
export function useVisualViewportInset(): void {
  useEffect(() => subscribe(() => {}), [])
}

/** True while an on-screen keyboard is (very probably) up — see
 *  `keyboardLikelyOpen`. Re-renders only when that answer flips. */
export function useKeyboardOpen(): boolean {
  return useSyncExternalStore(subscribe, () => snap.open, () => false)
}
